import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";

const blocks = [
  {
    labels: ["Programme vidéo", "Achat unique"],
    text: "Le monde se réorganise. Pour un investisseur, tout commence par savoir où regarder. Cinq actes pour lire les rapports de force, la technologie et les capitaux de la prochaine décennie.",
    line: "ACCÈS À VIE",
    to: "/multipolar",
    img: "/images/hero-1024.webp",
    imgAlt: "Silhouette face à un globe terrestre, MULTIPOLAR",
    testid: "home-block-multipolar",
    accTitle: "Mon programme",
    accName: "MULTIPOLAR",
    cardTitle: "Mon programme",
  },
  {
    labels: ["Groupe privé", "Abonnement mensuel"],
    text: "Accède à mon espace de travail et entre au cœur de mon activité d'investisseur : mon portefeuille complet, mes achats et mes ventes, et mes analyses au fil de l'actualité.",
    line: "SANS ENGAGEMENT",
    to: "/openaccess",
    img: "/images/openaccess-1024.webp",
    imgAlt: "Investisseur face à ses écrans de trading, OPENACCESS",
    testid: "home-block-portfolio",
    accTitle: "Mon portefeuille",
    accName: "OPENACCESS",
    cardTitle: "Mon portefeuille",
  },
];

function BlockLabels({ b }) {
  return (
    <div className="flex flex-nowrap md:flex-wrap items-center gap-2 px-8 py-4 border-b border-white/10 max-md:px-4">
      {b.labels.map((l) => (
        <span key={l} className="micro-label whitespace-nowrap max-md:!text-[8px] max-md:!tracking-[0.15em] max-md:!px-1.5">{l}</span>
      ))}
    </div>
  );
}

function BlockBody({ b, innerClass, buttonSuffix = "" }) {
  return (
    <>
      <BlockLabels b={b} />
      <img
        src={b.img}
        alt={b.imgAlt}
        width={1024}
        height={576}
        loading="lazy"
        decoding="async"
        className="w-full aspect-video object-cover"
      />
      <div className={`flex flex-col flex-1 ${innerClass}`}>
        <p className="font-display italic text-xl md:text-2xl text-[#FF6B00] mb-3">{b.cardTitle}</p>
        <p className="!text-[15px] text-[#D8D4CC] font-light">{b.text}</p>
        <p className="mt-6 !text-xs font-mono tracking-[0.2em] uppercase text-[#FF6B00]">{b.line}</p>
        <div className="flex-1" aria-hidden="true" />
        <Link
          to={b.to}
          data-testid={`${b.testid}-button${buttonSuffix}`}
          className="mt-7 w-full bg-[#D92D20] hover:bg-[#b3241a] text-white font-mono text-sm tracking-[0.2em] uppercase py-4 rounded-sm transition-[background-color,transform] duration-300 hover:scale-[1.01] flex items-center justify-center"
        >
          Découvrir
        </Link>
      </div>
    </>
  );
}

export default function HomeBlocks() {
  const [open, setOpen] = useState(null);

  return (
    <section data-testid="home-blocks" className="pb-24 md:pb-32 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Mobile uniquement (<768px) : lignes dépliantes, fermées par défaut */}
        <div className="md:hidden flex flex-col gap-4" data-testid="home-blocks-mobile">
          {blocks.map((b, i) => {
            const isOpen = open === i;
            return (
              <motion.div
                key={b.testid}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.5 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                className="border border-white/15 bg-white/[0.02] rounded-sm overflow-hidden"
                data-testid={`home-acc-row-${i}`}
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  data-testid={`home-acc-trigger-${i}`}
                  className="w-full flex items-center justify-between gap-4 px-5 py-5 min-h-[76px] text-left"
                >
                  <span className="flex flex-col items-start gap-2.5">
                    <span className="font-display text-[26px] leading-[1.1] text-[#F5F5F0]">{b.accTitle}</span>
                    <span className="micro-label">{b.accName}</span>
                  </span>
                  <motion.span
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    className="shrink-0 w-9 h-9 border border-white/20 rounded-full flex items-center justify-center text-[#F5F5F0]"
                    data-testid={`home-acc-icon-${i}`}
                  >
                    <Plus size={16} strokeWidth={1.5} />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                      data-testid={`home-acc-content-${i}`}
                    >
                      <div className="border-t border-white/10 flex flex-col">
                        <BlockBody b={b} innerClass="p-6" buttonSuffix="-mobile" />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Tablette et desktop (>=768px) : cartes d'origine, inchangées */}
        <div className="hidden md:grid grid-cols-2 gap-6 items-stretch">
          {blocks.map((b, i) => (
            <motion.div
              key={b.testid}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12 }}
              className="border border-white/15 bg-white/[0.02] rounded-sm flex flex-col h-full"
              data-testid={b.testid}
            >
              <BlockBody b={b} innerClass="p-8 md:p-10" />
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
